import Alpine from 'alpinejs';

const VOTE_DRAFT_COOKIE = 'oscars_vote_draft';
const SUBMITTED_COOKIE = 'oscars_submitted';
const COOKIE_MAX_AGE_DAYS = 32;
// Minuit le 23 mars 2026 en UTC : même instant pour tous les fuseaux
const VOTE_END_DATE = new Date(Date.UTC(2026, 2, 16, 0, 0, 0));

/**
 * Returns true if we are after the vote end date (votes finished).
 */
function isVoteEnded() {
  return new Date() >= VOTE_END_DATE;
}

/**
 * Set the "submitted" cookie so the end page shows the submitted state.
 * Cookie expires at VOTE_END_DATE (browser will remove it automatically).
 */
function setSubmittedCookie() {
  const maxAgeSeconds = Math.max(0, Math.floor((VOTE_END_DATE - Date.now()) / 1000));
  if (maxAgeSeconds > 0) {
    document.cookie = `${SUBMITTED_COOKIE}=1; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
  }
}

/**
 * Returns true if the submitted cookie is present and vote end date has not passed.
 * Clears the cookie if vote has ended (cleanup).
 */
function getSubmittedCookie() {
  if (isVoteEnded()) {
    document.cookie = `${SUBMITTED_COOKIE}=; path=/; max-age=0`;
    return false;
  }
  return document.cookie.includes(`${SUBMITTED_COOKIE}=`);
}


/**
 * Read vote draft from cookie. Returns { currentView, votes } or null if invalid/missing.
 */
function getVoteDraftCookie() {
  const match = document.cookie.match(new RegExp(`(^| )${VOTE_DRAFT_COOKIE}=([^;]+)`));
  if (!match) return null;
  try {
    const data = JSON.parse(decodeURIComponent(match[2]));
    if (typeof data.currentView !== 'number' || !Array.isArray(data.votes)) return null;
    return { currentView: data.currentView, votes: data.votes };
  } catch {
    return null;
  }
}

/**
 * Save vote draft to cookie.
 */
function setVoteDraftCookie(draft) {
  const value = encodeURIComponent(JSON.stringify({ currentView: draft.currentView, votes: draft.votes }));
  const maxAge = 60 * 60 * 24 * COOKIE_MAX_AGE_DAYS;
  document.cookie = `${VOTE_DRAFT_COOKIE}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

/**
 * Clear the vote draft cookie (e.g. after successful submission).
 */
function clearVoteDraftCookie() {
  document.cookie = `${VOTE_DRAFT_COOKIE}=; path=/; max-age=0`;
}

/**
 * Submit votes to the API.
 * Expects: email, votes, locale?, marketingOptedIn? (boolean for legal2 / marketing checkbox)
 */
export async function submitVote(email, votes, locale, marketingOptedIn) {
  const loc = locale ?? document.body.dataset.locale ?? 'fr';
  const res = await fetch('/api/vote', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email.trim().toLowerCase(), votes, locale: loc, marketingOptedIn: marketingOptedIn === true }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  clearVoteDraftCookie();
  return data;
}

// Expose vote helpers via Alpine store so templates use $store.vote
if (typeof Alpine !== 'undefined') {
  Alpine.store('vote', {
    isEnded: isVoteEnded,
    getDraft: getVoteDraftCookie,
    setDraft: setVoteDraftCookie,
    getSubmittedCookie,
    setSubmittedCookie,
    submit: submitVote,
  });
}

/**
 * Alpine component for a vote category slide. Receives items; categoryId is read from data-category-id on the root element.
 */
if (typeof Alpine !== 'undefined') {
  Alpine.data('voteCategory', (items) => ({
    selectedIndex: null,
    showModal: false,
    voteEnded: false,
    partnersRowVisible: 0,
    items: items ?? [],
    categoryId: '',
    closeModal() {
      this.showModal = false;
    },
    getItemPercentage(itemId) {
      const store = this.$store.voteResults;
      if (!store?.categories) return '–';
      const cat = store.categories.find((c) => c.id === this.categoryId);
      if (!cat?.items) return '–';
      const it = cat.items.find((i) => String(i.id) === String(itemId));
      return it?.percentage ?? '–';
    },
    isWinner(itemId) {
      const store = this.$store.voteResults;
      if (!store?.categories) return false;
      const cat = store.categories.find((c) => c.id === this.categoryId);
      if (!cat?.items?.length) return false;
      const winner = cat.items.reduce((best, it) => {
        const p = typeof it.percentage === 'string' ? parseFloat(it.percentage) : (it.percentage ?? 0);
        const bestP = typeof best.percentage === 'string' ? parseFloat(best.percentage) : (best.percentage ?? 0);
        return p > bestP ? it : best;
      });
      return String(winner.id) === String(itemId);
    },
    init() {
      this.categoryId = this.$el.dataset.categoryId ?? '';
      this.voteEnded = this.$store.vote?.isEnded?.() ?? false;
      const draft = this.$store.vote?.getDraft?.() ?? null;
      const saved = draft?.votes?.find((v) => v.categoryId === this.categoryId);
      if (saved?.selectedItem && this.items?.length) {
        const idx = this.items.findIndex(
          (item) =>
            (item.id && String(item.id) === String(saved.selectedItem.id)) ||
            (item.uid && item.uid === saved.selectedItem.uid)
        );
        if (idx >= 0) this.selectedIndex = idx;
      }
      const partners = this.$store.partners?.shuffled ?? [];
      if (partners.length >= 3) {
        setInterval(() => {
          this.partnersRowVisible = this.partnersRowVisible === 0 ? 1 : 0;
        }, 4000);
      }
      if (!window.__voteResultsFetched) {
        window.__voteResultsFetched = true;
        fetch('/api/results?locale=' + (document.body.dataset.locale || 'fr') + '&full=1')
          .then((r) => (r.ok ? r.json() : {}))
          .then((data) => {
            if (data.categories) Alpine.store('voteResults', data);
          })
          .catch(() => {});
      }
    },
  }));
}

function getLocale() {
  return document.body.dataset.locale || 'fr';
}

function scrollTo(el) {
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function initScrollTo() {
  document.querySelectorAll('.scroll-to').forEach((link) => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const target = document.querySelector(link.getAttribute('href'));
      scrollTo(target);
    });
  });
}

const COPY_MESSAGES = {
  'pt-br': 'Link copiado',
  br: 'Link copiado',
  de: 'Link kopiert',
  fr: 'Lien copié dans le presse-papier',
  es: 'Enlace copiado',
  mx: 'Enlace copiado',
};

document.addEventListener('click', (e) => {
  const btn = e.target.closest('#copytoclipboard');
  if (!btn) return;
  e.preventDefault();
  navigator.clipboard.writeText(window.location.origin).then(() => {
    alert(COPY_MESSAGES[getLocale()] || 'Link copied');
  });
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initScrollTo);
} else {
  initScrollTo();
}

Alpine.data('choices', () => ({
  loading: false,
  submit(event) {
    event.preventDefault();
    const form = event.target;
    const email = (form.email?.value ?? form.querySelector('[name="email"]')?.value)?.trim();
    const votesInput = form.querySelector('[name="votes"]');
    const votes = votesInput ? JSON.parse(votesInput.value || '[]') : [];
    if (!email || !votes.length) return;
    this.loading = true;

    this.$store.vote.submit(email, votes)
      .then(() => {
        this.loading = false;
        window.location.href = '/thanks.html';
      })
      .catch((err) => {
        this.loading = false;
        console.error(err);
        window.location.href = '/error.html';
      });
  },
}));

Alpine.data('register', () => ({
  loading: false,
  submit(event) {
    this.loading = true;
    event.preventDefault();
    const form = event.target;

    fetch('/api/mail', {
      method: 'POST',
      body: new URLSearchParams(new FormData(form)),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Erreur dans la requête');
        return res.json();
      })
      .then((data) => {
        this.loading = false;
        if (data.error) throw new Error(data.error);
        window.location.href = data.redirectUrl || '/';
      })
      .catch((err) => {
        this.loading = false;
        console.error(err);
        window.location.href = '/error.html';
      });
  },
}));
